import { useEffect } from "react";
import {
  CONSENT_UPDATED_EVENT,
  readConsent,
} from "@shared/CookieConsent/CookieConsent";
import {
  AHREFS_KEY,
  ANALYTICS_SCRIPT_IDS,
  GA_MEASUREMENT_ID,
} from "@lib/utils/analyticsIds";

// This component owns every third-party tag on nougat.law. Tag ids come from
// @lib/utils/analyticsIds -- never inline a container id here.
//
// public/index.html already boots GA4 in <head> (so measurement does not wait
// on React) and marks it with window.__juristaiAnalyticsInitialized. We honour
// that flag rather than issuing a second gtag('config'), which would double
// every page_view.
//
// GA4 and Ahrefs load on the analytics tier. Consent Mode v2 gates advertising
// data: ad_storage / ad_user_data / ad_personalization stay "denied" until the
// visitor allows marketing cookies; analytics_storage is granted.
//
// Do NOT gate the container load itself on marketingPixels. That was the
// regression that silently killed every GTM-configured tag (and Ahrefs) for
// the ~all of traffic that never clicks "Allow marketing".

const hasMarketingConsent = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(readConsent()?.marketingPixels);
};

const injectScript = (id, src, attributes = {}) => {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;

  Object.entries(attributes).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });

  document.head.appendChild(script);
};

function ThirdPartyScripts() {
  useEffect(() => {
    const syncMarketingConsent = () => {
      if (typeof window.gtag !== "function") {
        return;
      }

      const isMarketingGranted = hasMarketingConsent();

      window.gtag("consent", "update", {
        ad_storage: isMarketingGranted ? "granted" : "denied",
        ad_user_data: isMarketingGranted ? "granted" : "denied",
        ad_personalization: isMarketingGranted ? "granted" : "denied",
      });
    };

    const loadThirdPartyScripts = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag =
        window.gtag ||
        function gtag() {
          window.dataLayer.push(arguments);
        };

      // Only bootstrap GA4 if the <head> snippet did not already do it.
      if (GA_MEASUREMENT_ID && !window.__juristaiAnalyticsInitialized) {
        window.gtag("consent", "default", {
          analytics_storage: "granted",
          ad_storage: "denied",
          ad_user_data: "denied",
          ad_personalization: "denied",
        });
        window.gtag("js", new Date());
        window.gtag("config", GA_MEASUREMENT_ID);
        injectScript(
          ANALYTICS_SCRIPT_IDS.googleAnalytics,
          `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
        );
        window.__juristaiAnalyticsInitialized = true;
      }

      syncMarketingConsent();

      // Ahrefs Web Analytics is cookieless, so it rides the analytics tier.
      injectScript(
        ANALYTICS_SCRIPT_IDS.ahrefs,
        "https://analytics.ahrefs.com/analytics.js",
        { "data-key": AHREFS_KEY }
      );
    };

    // Scheduled off the critical path, but not behind window.load: waiting for
    // load + a 2.5s idle deadline dropped every short/bounced session from GA.
    let handle;

    if ("requestIdleCallback" in window) {
      handle = window.requestIdleCallback(loadThirdPartyScripts, {
        timeout: 500,
      });
    } else {
      handle = window.setTimeout(loadThirdPartyScripts, 0);
    }

    window.addEventListener(CONSENT_UPDATED_EVENT, syncMarketingConsent);

    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, syncMarketingConsent);
      if ("cancelIdleCallback" in window && typeof handle === "number") {
        window.cancelIdleCallback(handle);
      } else if (typeof handle === "number") {
        window.clearTimeout(handle);
      }
    };
  }, []);

  return null;
}

export default ThirdPartyScripts;
